import { isValidEmailAddress } from '@/lib/utils/isValidEmailAddress';
import type { BillingAddressFormData, JpFormData, ShippingAddressFormData } from '@/lib/checkout';
import type { GiftFormData } from '@/components/checkout';
import { isValidPhoneNumber } from '@/lib/phone';
import { COUNTRY_CALLING_CODES } from '@/lib/phonePrefixes/const';

type FieldErrors = Record<string, string>;

interface ShippingValidationParams {
  formData: ShippingAddressFormData;
  jpFormData: JpFormData;
  isJP: boolean;
  isBoutique: boolean;
  selectedBoutiqueId: string;
  shippingTitle: string;
  phoneNumber: string;
  phonePrefixCode: string;
  isGift: boolean;
  giftData: GiftFormData;
  tRequired: string;
  tInvalidEmail: string;
  tInvalidPhoneNumber: string;
  countryAreaRequired?: boolean;
}

function setRequiredWhenEmpty(
  errors: FieldErrors,
  field: string,
  value: string | undefined,
  message: string
): void {
  if (!value) {
    errors[field] = message;
  }
}

function setRequiredWhenBlank(
  errors: FieldErrors,
  field: string,
  value: string,
  message: string
): void {
  if (!value.trim()) {
    errors[field] = message;
  }
}

function validateShippingEmail(
  email: string | undefined,
  tRequired: string,
  tInvalidEmail: string
): FieldErrors {
  if (!email) {
    return { email: tRequired };
  }

  if (!isValidEmailAddress(email)) {
    return { email: tInvalidEmail };
  }

  return {};
}

function validateShippingPhone({
  formData,
  isJP,
  phoneNumber,
  phonePrefixCode,
  tRequired,
  tInvalidPhoneNumber,
}: Pick<
  ShippingValidationParams,
  'formData' | 'isJP' | 'phoneNumber' | 'phonePrefixCode' | 'tRequired' | 'tInvalidPhoneNumber'
>): FieldErrors {
  if (isJP) {
    return formData.phone ? {} : { phone: tRequired };
  }

  if (!phoneNumber.trim()) {
    return { phoneNumber: tRequired };
  }

  const prefixEntry = COUNTRY_CALLING_CODES.find((country) => country.code === phonePrefixCode);
  if (!prefixEntry) {
    return {};
  }

  const fullNumber = `${prefixEntry.value}${phoneNumber}`.replaceAll(/\s+/g, '');
  return isValidPhoneNumber(fullNumber) ? {} : { phoneNumber: tInvalidPhoneNumber };
}

function validateBoutiquePickup(
  isBoutique: boolean,
  selectedBoutiqueId: string,
  tRequired: string
): FieldErrors {
  return isBoutique && !selectedBoutiqueId ? { boutiquePickup: tRequired } : {};
}

function validateStandardShippingAddress(
  formData: ShippingAddressFormData,
  shippingTitle: string,
  isJP: boolean,
  tRequired: string,
  countryAreaRequired = false
): FieldErrors {
  const errors: FieldErrors = {};

  if (!isJP && !shippingTitle) {
    errors.title = tRequired;
  }

  setRequiredWhenEmpty(errors, 'firstName', formData.firstName, tRequired);
  setRequiredWhenEmpty(errors, 'lastName', formData.lastName, tRequired);
  setRequiredWhenEmpty(errors, 'streetAddress1', formData.streetAddress1, tRequired);
  setRequiredWhenEmpty(errors, 'city', formData.city, tRequired);
  setRequiredWhenEmpty(errors, 'postalCode', formData.postalCode, tRequired);
  setRequiredWhenEmpty(errors, 'country', formData.country, tRequired);

  if (countryAreaRequired) {
    setRequiredWhenEmpty(errors, 'countryArea', formData.countryArea, tRequired);
  }

  return errors;
}

function validateJpShippingFields(jpFormData: JpFormData, tRequired: string): FieldErrors {
  const errors: FieldErrors = {};

  setRequiredWhenEmpty(errors, 'salutation', jpFormData.salutation, tRequired);
  setRequiredWhenEmpty(errors, 'lastNameKatakana', jpFormData.lastNameKatakana, tRequired);
  setRequiredWhenEmpty(errors, 'firstNameKatakana', jpFormData.firstNameKatakana, tRequired);
  setRequiredWhenEmpty(errors, 'lastNameRomaji', jpFormData.lastNameRomaji, tRequired);
  setRequiredWhenEmpty(errors, 'firstNameRomaji', jpFormData.firstNameRomaji, tRequired);

  return errors;
}

function validateGiftFields(giftData: GiftFormData, tRequired: string): FieldErrors {
  const errors: FieldErrors = {};

  setRequiredWhenBlank(errors, 'giftFrom', giftData.giftFrom, tRequired);
  setRequiredWhenBlank(errors, 'giftMessage', giftData.giftMessage, tRequired);

  return errors;
}

interface ShippingAddressBookValidationParams {
  formData: ShippingAddressFormData;
  jpFormData: JpFormData;
  isJP: boolean;
  shippingTitle: string;
  tRequired: string;
  countryAreaRequired?: boolean;
}

/**
 * Validates shipping address fields for save-to-address-book only.
 * Excludes email, phone, gift, and boutique checks used on Continue.
 */
export const computeShippingAddressBookErrors = ({
  formData,
  jpFormData,
  isJP,
  shippingTitle,
  tRequired,
  countryAreaRequired = false,
}: ShippingAddressBookValidationParams): FieldErrors => {
  const errors = validateStandardShippingAddress(
    formData,
    shippingTitle,
    isJP,
    tRequired,
    countryAreaRequired
  );

  if (isJP) {
    Object.assign(errors, validateJpShippingFields(jpFormData, tRequired));
  }

  return errors;
};

/**
 * Computes shipping form validation errors without side effects.
 * Returns an empty object when all fields are valid.
 */
export const computeShippingErrors = (params: ShippingValidationParams): FieldErrors => {
  const {
    formData,
    jpFormData,
    isJP,
    isBoutique,
    selectedBoutiqueId,
    shippingTitle,
    phoneNumber,
    phonePrefixCode,
    isGift,
    giftData,
    tRequired,
    tInvalidEmail,
    tInvalidPhoneNumber,
    countryAreaRequired = false,
  } = params;

  const errors: FieldErrors = {
    ...validateShippingEmail(formData.email, tRequired, tInvalidEmail),
    ...validateShippingPhone({
      formData,
      isJP,
      phoneNumber,
      phonePrefixCode,
      tRequired,
      tInvalidPhoneNumber,
    }),
    ...validateBoutiquePickup(isBoutique, selectedBoutiqueId, tRequired),
  };

  if (!isBoutique) {
    Object.assign(
      errors,
      computeShippingAddressBookErrors({
        formData,
        jpFormData,
        isJP,
        shippingTitle,
        tRequired,
        countryAreaRequired,
      })
    );
  }

  if (isGift) {
    Object.assign(errors, validateGiftFields(giftData, tRequired));
  }

  return errors;
};

interface BillingValidationParams {
  billingData: BillingAddressFormData;
  billingTitle: string;
  tRequired: string;
  countryAreaRequired?: boolean;
}

/**
 * Computes billing form validation errors without side effects.
 * Returns an empty object when all fields are valid.
 */
export const computeBillingErrors = ({
  billingData,
  billingTitle,
  tRequired,
  countryAreaRequired = false,
}: BillingValidationParams): FieldErrors => {
  const errors: FieldErrors = {};

  setRequiredWhenEmpty(errors, 'title', billingTitle, tRequired);
  setRequiredWhenEmpty(errors, 'firstName', billingData.firstName, tRequired);
  setRequiredWhenEmpty(errors, 'lastName', billingData.lastName, tRequired);
  setRequiredWhenEmpty(errors, 'streetAddress1', billingData.streetAddress1, tRequired);
  setRequiredWhenEmpty(errors, 'city', billingData.city, tRequired);
  setRequiredWhenEmpty(errors, 'postalCode', billingData.postalCode, tRequired);
  setRequiredWhenEmpty(errors, 'country', billingData.country, tRequired);

  if (countryAreaRequired) {
    setRequiredWhenEmpty(errors, 'countryArea', billingData.countryArea, tRequired);
  }

  return errors;
};

interface ApplyDeliverySubmitValidationErrorsParams extends ShippingAddressBookValidationParams {
  shippingErrors: FieldErrors;
  billingValidationErrors: FieldErrors;
  showBillingForm: boolean;
  isBoutique: boolean;
  setIsEmailUnlocked: (value: boolean) => void;
  setFormErrors: (errors: FieldErrors) => void;
  setBillingErrors: (errors: FieldErrors) => void;
  setShippingViewModeForm: () => void;
  setBillingViewModeForm: () => void;
  scrollToFirstInvalid: () => void;
}

/**
 * Applies delivery-step validation errors to form state and address book UI.
 * Returns true when validation failed and the submit flow should stop.
 */
export const applyDeliverySubmitValidationErrors = (
  params: ApplyDeliverySubmitValidationErrorsParams
): boolean => {
  const {
    shippingErrors,
    billingValidationErrors,
    showBillingForm,
    isBoutique,
    formData,
    jpFormData,
    isJP,
    shippingTitle,
    tRequired,
    countryAreaRequired,
    setIsEmailUnlocked,
    setFormErrors,
    setBillingErrors,
    setShippingViewModeForm,
    setBillingViewModeForm,
    scrollToFirstInvalid,
  } = params;

  const hasShippingErrors = Object.keys(shippingErrors).length > 0;
  const hasBillingErrors = Object.keys(billingValidationErrors).length > 0;

  if (!hasShippingErrors && !hasBillingErrors) {
    return false;
  }

  if (shippingErrors.email) {
    setIsEmailUnlocked(true);
  }
  setFormErrors(shippingErrors);
  if (showBillingForm) {
    setBillingErrors(billingValidationErrors);
  }

  if (!isBoutique) {
    const shippingAddressFieldErrors = computeShippingAddressBookErrors({
      formData,
      jpFormData,
      isJP,
      shippingTitle,
      tRequired,
      countryAreaRequired,
    });
    if (Object.keys(shippingAddressFieldErrors).some((key) => shippingErrors[key])) {
      setShippingViewModeForm();
    }
  }
  if (hasBillingErrors) {
    setBillingViewModeForm();
  }

  scrollToFirstInvalid();
  return true;
};
